/**
 * Skin decoding (W2-01b; ARCHITECTURE.md §12, skin-and-assets.md S§2-S§4): turns a validated respack
 * (`RespackFiles`, from formats/respack `loadRespack`) into a `LoadedSkin` the renderer and the audio engine use:
 *
 *  - the nine images are decoded to straight-alpha ImageBitmaps (`premultiplyAlpha: 'none'`, no colour-space
 *    conversion and no EXIF orientation, as Phira's image decoder reads them; the content may be PNG, JPEG, GIF,
 *    BMP or WebP whatever the `.png` name says);
 *  - texture sizes come from the image headers (the same sizes Phira validates the hold atlas against) and give the
 *    `SkinMetrics` through formats/respack `skinMetricsFrom`;
 *  - with `holdRepeat` the hold bodies are cut out as separate bitmaps: rows [A0, H - A1) of hold.png and
 *    hold_mh.png (tail slice on top, head slice at the bottom, S§3.4), which the renderer tiles with REPEAT;
 *  - the click/drag/flick sounds are passed on encoded (the audio engine decodes them; `ending` is not used by a
 *    chart preview, and a missing sound falls back to the audio engine's synthesised one).
 *
 * `checkPack` re-runs the pack-level checks of Phira's validation on a `RespackFiles` (installed packs come back from
 * storage; nothing that fails them is ever decoded).
 */

import type { MessageRef } from '../../core/util/types';
import { skinMetricsFrom } from '../../formats/respack/metrics';
import {
  RESPACK_IMAGES,
  type RespackFiles,
  type RespackImageName,
  type ResPackInfo,
} from '../../formats/respack/types';
import { sniffImage, sniffSound, verifyRespackInfo, type RespackImageFormat } from '../../formats/respack/validate';
import type { LoadedSkin } from '../types';
import { m } from './skin.messages';

export type ImageSize = readonly [width: number, height: number];
/** Source rectangle [x, y, width, height] in image pixels. */
export type CropRect = readonly [number, number, number, number];
export type SkinSounds = LoadedSkin['sounds'];

/** A skin problem with its translatable reasons (the first one is the headline). */
export class SkinError extends Error {
  readonly refs: readonly MessageRef[];
  constructor(refs: MessageRef | readonly MessageRef[]) {
    const list: readonly MessageRef[] = Array.isArray(refs) ? refs : [refs as MessageRef];
    super(list.map((r) => r.key).join(', ') || 'skin error');
    this.name = 'SkinError';
    this.refs = list;
  }
}

/** The reasons of any error: SkinError refs, else `fallback` with the error text as {detail}. */
export function errorRefs(
  e: unknown,
  fallback: string,
  params: Readonly<Record<string, string | number>> = {},
): MessageRef[] {
  if (e instanceof SkinError && e.refs.length > 0) return [...e.refs];
  const detail = e instanceof Error ? e.message : String(e);
  return [{ key: fallback, params: { ...params, detail } }];
}

/** Decodes encoded image bytes into a straight-alpha bitmap (optionally only a part of the image). */
export interface SkinImageDecoder {
  decode(bytes: Uint8Array, mime: string, crop?: CropRect): Promise<ImageBitmap>;
}

const MIME: Readonly<Record<RespackImageFormat, string>> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
};

/** Bytes as a BlobPart backed by a plain ArrayBuffer (copies only when the view is backed by something else). */
function blobPart(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return bytes.buffer instanceof ArrayBuffer ? (bytes as Uint8Array<ArrayBuffer>) : new Uint8Array(bytes);
}

const BITMAP_OPTIONS: ImageBitmapOptions = {
  premultiplyAlpha: 'none',
  colorSpaceConversion: 'none',
  imageOrientation: 'none',
};

/** createImageBitmap on a Blob of the bytes (browser default). */
export const browserImageDecoder: SkinImageDecoder = {
  decode(bytes, mime, crop) {
    const blob = new Blob([blobPart(bytes)], { type: mime });
    return crop === undefined
      ? createImageBitmap(blob, BITMAP_OPTIONS)
      : createImageBitmap(blob, crop[0], crop[1], crop[2], crop[3], BITMAP_OPTIONS);
  },
};

/** Rows [tail, height - head) of a hold image: the body slice tiled with holdRepeat. */
export function holdBodyRect(size: ImageSize, atlas: readonly [tailPx: number, headPx: number]): CropRect {
  return [0, atlas[0], size[0], Math.max(0, size[1] - atlas[0] - atlas[1])];
}

/**
 * Phira's pack-level rules on a parsed pack (S§2.1-S§2.3): info.yml verify(), every image decodable from its header,
 * hold atlas tail + head < image height (normal and MH), every present sound decodable.
 */
export function checkPack(pack: RespackFiles): MessageRef[] {
  const errors: MessageRef[] = [...verifyRespackInfo(pack.info)];
  const heights: Partial<Record<RespackImageName, number>> = {};
  for (const name of RESPACK_IMAGES) {
    const file = `${name}.png`;
    const bytes = (pack.images as Partial<Record<RespackImageName, Uint8Array>>)[name];
    if (bytes === undefined) {
      errors.push({ key: 'respack.error.missingImage', params: { file } });
      continue;
    }
    const header = sniffImage(bytes);
    if (header === null) errors.push({ key: 'respack.error.imageUndecodable', params: { file } });
    else heights[name] = header.height;
  }
  const atlases: ReadonlyArray<readonly [RespackImageName, readonly [number, number]]> = [
    ['hold', pack.info.holdAtlas],
    ['hold_mh', pack.info.holdAtlasMH],
  ];
  for (const [name, [tail, head]] of atlases) {
    const height = heights[name];
    if (height !== undefined && !(tail + head < height)) {
      errors.push({ key: 'respack.error.holdAtlas', params: { file: `${name}.png`, tail, head, height } });
    }
  }
  for (const [name, sound] of Object.entries(pack.sounds)) {
    if (sniffSound(sound.bytes) === null) {
      errors.push({ key: 'respack.error.soundUndecodable', params: { file: `${name}.${sound.ext}` } });
    }
  }
  return errors;
}

/** Header sizes of the nine images (throws a SkinError for an image whose header cannot be read). */
export function packImageSizes(pack: RespackFiles): Record<RespackImageName, ImageSize> {
  const sizes = {} as Record<RespackImageName, ImageSize>;
  const bad: MessageRef[] = [];
  for (const name of RESPACK_IMAGES) {
    const header = sniffImage(pack.images[name]);
    if (header === null) bad.push({ key: 'respack.error.imageUndecodable', params: { file: `${name}.png` } });
    else sizes[name] = [header.width, header.height];
  }
  if (bad.length > 0) throw new SkinError(bad);
  return sizes;
}

/** Closes every bitmap of a skin (only for skins nobody else holds). */
export function closeSkin(skin: LoadedSkin): void {
  const seen = new Set<ImageBitmap>();
  for (const name of RESPACK_IMAGES) seen.add(skin.images[name]);
  if (skin.holdBody !== null) seen.add(skin.holdBody);
  if (skin.holdBodyMh !== null) seen.add(skin.holdBodyMh);
  for (const b of seen) b.close();
}

export interface SkinIdentity {
  readonly id: string;
  readonly source: LoadedSkin['source'];
  /** Display name; default: info.yml `name`. */
  readonly name?: string;
}

/** Assembles a LoadedSkin from decoded parts (shared by respacks and the procedural skin). */
export function assembleSkin(parts: {
  readonly identity: SkinIdentity;
  readonly info: ResPackInfo;
  readonly sizes: Readonly<Record<RespackImageName, ImageSize>>;
  readonly images: Readonly<Record<RespackImageName, ImageBitmap>>;
  readonly holdBody: ImageBitmap | null;
  readonly holdBodyMh: ImageBitmap | null;
  readonly sounds: SkinSounds;
}): LoadedSkin {
  return {
    id: parts.identity.id,
    name: parts.identity.name ?? parts.info.name,
    source: parts.identity.source,
    metrics: skinMetricsFrom(parts.info, parts.sizes),
    images: parts.images,
    holdBody: parts.holdBody,
    holdBodyMh: parts.holdBodyMh,
    sounds: parts.sounds,
  };
}

/** The click/drag/flick sounds of a pack (encoded; null when the pack has none). */
export function packSounds(pack: RespackFiles): SkinSounds {
  const pick = (name: 'click' | 'drag' | 'flick') => {
    const s = pack.sounds[name];
    return s === undefined ? null : { bytes: s.bytes, ext: s.ext };
  };
  return { click: pick('click'), drag: pick('drag'), flick: pick('flick') };
}

/**
 * Decodes a validated pack. Throws a SkinError (messages `respack.*` / `skin.imageUndecodable`) when the pack breaks
 * Phira's rules or the browser cannot decode an image; no bitmap is leaked on failure.
 */
export async function decodeSkin(
  pack: RespackFiles,
  identity: SkinIdentity,
  decoder: SkinImageDecoder = browserImageDecoder,
): Promise<LoadedSkin> {
  const problems = checkPack(pack);
  if (problems.length > 0) throw new SkinError(problems);
  const sizes = packImageSizes(pack);
  const mime = (name: RespackImageName): string => {
    const header = sniffImage(pack.images[name]);
    return header === null ? 'application/octet-stream' : MIME[header.format];
  };

  const jobs: Array<{ readonly label: string; readonly run: () => Promise<ImageBitmap> }> = RESPACK_IMAGES.map(
    (name) => ({ label: `${name}.png`, run: () => decoder.decode(pack.images[name], mime(name)) }),
  );
  const { holdRepeat, holdAtlas, holdAtlasMH } = pack.info;
  if (holdRepeat) {
    jobs.push(
      {
        label: 'hold.png',
        run: () => decoder.decode(pack.images.hold, mime('hold'), holdBodyRect(sizes.hold, holdAtlas)),
      },
      {
        label: 'hold_mh.png',
        run: () => decoder.decode(pack.images.hold_mh, mime('hold_mh'), holdBodyRect(sizes.hold_mh, holdAtlasMH)),
      },
    );
  }
  const settled = await Promise.allSettled(jobs.map((j) => j.run()));
  const failed: MessageRef[] = [];
  const bitmaps: ImageBitmap[] = [];
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled') bitmaps.push(r.value);
    else failed.push({ key: m.imageUndecodable, params: { file: jobs[i]?.label ?? '' } });
  });
  if (failed.length > 0) {
    for (const b of bitmaps) b.close();
    throw new SkinError(failed);
  }

  const images = {} as Record<RespackImageName, ImageBitmap>;
  RESPACK_IMAGES.forEach((name, i) => {
    images[name] = bitmaps[i] as ImageBitmap;
  });
  return assembleSkin({
    identity,
    info: pack.info,
    sizes,
    images,
    holdBody: holdRepeat ? (bitmaps[RESPACK_IMAGES.length] ?? null) : null,
    holdBodyMh: holdRepeat ? (bitmaps[RESPACK_IMAGES.length + 1] ?? null) : null,
    sounds: packSounds(pack),
  });
}
